'use strict';

(function () {
    class cmsAnimationLibrary {
        constructor(options = {}) {
            // Defaults mirror the SCSS custom property defaults and can be overridden manually.
            this.defaults = {
                selector: '[data-cms-animate]',
                childSelector: '[data-cms-animate-child]',
                movement: 'none',
                scale: 'none',
                effect: 'fade',
                perspective: 'none',
                delay: 0,
                duration: 800,
                easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                threshold: 0.16,
                stagger: 0,
                distance: '2rem',
                once: true,
                repeat: false,
                minBreakpoint: null,
                maxBreakpoint: null,
                breakpoints: {
                    xs: 0,
                    sm: 576,
                    md: 768,
                    lg: 992,
                    xl: 1200,
                    xxl: 1400
                },
                rootMargin: '0px 0px -8% 0px',
                reducedMotionQuery: '(prefers-reduced-motion: reduce)'
            };

            this.settings = Object.assign({}, this.defaults, options);
            this.allowedValues = {
                movement: new Set(['none', 'up', 'down', 'left', 'right']),
                scale: new Set(['none', 'scale', 'zoom-in', 'zoom-out', 'soft-pop']),
                effect: new Set(['none', 'fade', 'blur', 'reveal']),
                perspective: new Set(['none', 'flip-up', 'flip-down', 'rotate-in'])
            };
            this.trackedElements = new Map();
            this.observers = new Map();
            this.pendingFrames = new WeakMap();
            this.reducedMotion = window.matchMedia(this.settings.reducedMotionQuery);
            this.isInitialised = false;
            this.isInert = false;
            this.isWatchingReducedMotion = false;
            this.isWatchingResize = false;
            this.resizeFrame = null;
            this.isCmsAnimationLibraryInstance = true;

            this.handleIntersection = this.handleIntersection.bind(this);
            this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
            this.handleResize = this.handleResize.bind(this);
        }

        init() {
            // init() is idempotent so CMS screens can call it after partial page renders.
            if (this.isInitialised) {
                this.refresh();
                return this;
            }

            this.isInitialised = true;

            if (!this.hasAnimationCandidates(document)) {
                this.isInert = true;
                return this;
            }

            this.isInert = false;
            this.watchReducedMotion();
            this.refresh();

            return this;
        }

        refresh(root = document) {
            // refresh() scans a root node so dynamically injected CMS content can be opted in.
            if (!root || !root.querySelectorAll) {
                return this;
            }

            if (!this.hasAnimationCandidates(root)) {
                return this;
            }

            if (this.isInert) {
                this.isInert = false;
                this.watchReducedMotion();
            }

            const elements = [
                ...(root.matches?.(this.settings.selector) ? [root] : []),
                ...Array.from(root.querySelectorAll(this.settings.selector))
            ];

            elements.forEach((element) => {
                this.observeElement(element);
            });

            return this;
        }

        observeElement(element) {
            if (!this.isValidElement(element)) {
                return this;
            }

            if (this.isInert) {
                this.isInert = false;
                this.watchReducedMotion();
            }

            // Existing elements keep their state, but updated data attributes are re-read.
            if (this.trackedElements.has(element)) {
                const previousSettings = this.trackedElements.get(element);
                const nextSettings = this.readSettings(element);

                this.applySettings(element, nextSettings);
                this.prepareElement(element, nextSettings);
                this.trackedElements.set(element, nextSettings);
                this.watchResizeForBreakpoints(nextSettings);

                if (previousSettings.threshold !== nextSettings.threshold) {
                    this.unobserveFromObserver(element, previousSettings);
                }

                if (!this.isAnimationEnabledForViewport(nextSettings)) {
                    this.unobserveFromObserver(element, nextSettings);
                    this.activateElement(element, nextSettings, true);
                    return this;
                }

                if (!this.reducedMotion.matches) {
                    this.getObserver(nextSettings.threshold).observe(element);
                }

                return this;
            }

            const settings = this.readSettings(element);
            this.applySettings(element, settings);
            this.prepareElement(element, settings);
            this.trackedElements.set(element, settings);
            this.watchResizeForBreakpoints(settings);

            if (!this.isAnimationEnabledForViewport(settings)) {
                this.activateElement(element, settings, true);
                return this;
            }

            if (!('IntersectionObserver' in window)) {
                this.activateElement(element, settings, true);
                return this;
            }

            if (this.reducedMotion.matches) {
                this.activateElement(element, settings, true);
                return this;
            }

            this.getObserver(settings.threshold).observe(element);
            this.activateIfInitiallyVisible(element, settings);

            return this;
        }

        unobserveElement(element) {
            // unobserveElement() stops viewport tracking without removing the element's data state.
            if (!this.trackedElements.has(element)) {
                return this;
            }

            this.cancelPendingFrame(element);
            this.stopObservingElement(element);

            return this;
        }

        destroy() {
            // destroy() leaves markup intact and only releases runtime observers and frame work.
            this.observers.forEach((observer) => {
                observer.disconnect();
            });

            this.trackedElements.forEach((settings, element) => {
                this.cancelPendingFrame(element);
            });

            this.trackedElements.clear();
            this.observers.clear();
            this.isInitialised = false;
            this.isInert = false;
            this.unwatchReducedMotion();
            this.unwatchResize();

            return this;
        }

        handleIntersection(entries) {
            // IntersectionObserver is the only scroll trigger; no scroll listeners are used.
            entries.forEach((entry) => {
                const element = entry.target;
                const settings = this.trackedElements.get(element);

                if (!settings) {
                    return;
                }

                if (!this.isAnimationEnabledForViewport(settings)) {
                    this.activateElement(element, settings, true);
                    this.unobserveFromObserver(element, settings);
                    return;
                }

                if (entry.isIntersecting) {
                    if (entry.intersectionRatio >= settings.threshold) {
                        this.activateElement(element, settings);
                        this.unobserveOnceElementAfterActivation(element, settings);

                        return;
                    }

                    element.setAttribute('data-animate-state', 'inactive');
                    return;
                }

                if (settings.repeat) {
                    this.deactivateElement(element, settings);
                }
            });
        }

        activateElement(element, settings, instant = false) {
            this.cancelPendingFrame(element);

            if (instant) {
                element.setAttribute('data-animate-state', 'active');
                this.activateChildren(element, settings, true);
                return;
            }

            // Two frames ensure inactive variables are committed before active state starts.
            const frame = window.requestAnimationFrame(() => {
                this.commitInactiveState(element);

                const activeFrame = window.requestAnimationFrame(() => {
                    element.setAttribute('data-animate-state', 'active');
                    this.activateChildren(element, settings);
                    this.pendingFrames.delete(element);
                });

                this.pendingFrames.set(element, activeFrame);
            });

            this.pendingFrames.set(element, frame);
        }

        deactivateElement(element, settings) {
            this.cancelPendingFrame(element);
            element.setAttribute('data-animate-state', 'inactive');
            this.deactivateChildren(element, settings);
        }

        reanimate(element) {
            if (!(element instanceof Element)) {
                return this;
            }

            const root = element.hasAttribute('data-cms-animate-child') && !element.hasAttribute('data-cms-animate')
                ? element
                : element.hasAttribute('data-cms-animate')
                    ? element
                    : element.closest(this.settings.selector);

            if (!root) {
                return this;
            }

            const parent = root.hasAttribute('data-cms-animate-child')
                ? root.closest(this.settings.selector)
                : null;
            const parentSettings = parent ? this.readSettings(parent) : null;
            const childIndex = parent ? this.getChildren(parent).indexOf(root) : 0;
            const settings = this.readSettings(root, parentSettings, Math.max(childIndex, 0));

            this.applySettings(root, settings);
            this.applyCategoryAttributes(root, settings);
            this.trackedElements.set(root, settings);
            this.watchResizeForBreakpoints(settings);
            this.replayTargets(root, settings);

            return this;
        }

        reanimateGroup(element) {
            return this.reanimate(element);
        }

        replayTargets(element, settings) {
            const targets = [element, ...this.getChildren(element)];

            targets.forEach((target, index) => {
                this.cancelPendingFrame(target);

                const targetSettings = target === element
                    ? settings
                    : this.readSettings(target, settings, index - 1);

                this.applySettings(target, targetSettings);
                this.applyCategoryAttributes(target, targetSettings);
                this.suppressTransition(target);
                target.setAttribute('data-animate-state', 'inactive');
                target.setAttribute('data-animate-replaying', 'true');
            });

            targets.forEach((target) => {
                this.commitInactiveState(target);
            });

            const frame = window.requestAnimationFrame(() => {
                targets.forEach((target) => {
                    target.removeAttribute('data-animate-replaying');
                    this.restoreTransition(target);
                    this.commitInactiveState(target);
                });

                const activeFrame = window.requestAnimationFrame(() => {
                    targets.forEach((target) => {
                        target.setAttribute('data-animate-state', 'active');
                        this.pendingFrames.delete(target);
                    });
                });

                targets.forEach((target) => {
                    this.pendingFrames.set(target, activeFrame);
                });
            });

            targets.forEach((target) => {
                this.pendingFrames.set(target, frame);
            });
        }

        activateIfInitiallyVisible(element, settings) {
            window.requestAnimationFrame(() => {
                if (!this.trackedElements.has(element)) {
                    return;
                }

                if (!this.isElementInActivationRange(element, settings.threshold)) {
                    return;
                }

                this.activateElement(element, settings);
                this.unobserveOnceElementAfterActivation(element, settings);
            });
        }

        unobserveOnceElementAfterActivation(element, settings) {
            if (!settings.once || settings.repeat) {
                return;
            }

            window.setTimeout(() => {
                this.stopObservingElement(element);
            }, settings.delay + settings.duration + 80);
        }

        activateChildren(element, settings, instant = false) {
            // Child animations inherit parent timing and add index-based stagger delay.
            const children = this.getChildren(element);

            children.forEach((child, index) => {
                const childSettings = this.readSettings(child, settings, index);
                this.applySettings(child, childSettings);
                this.applyCategoryAttributes(child, childSettings);
                this.watchResizeForBreakpoints(childSettings);

                if (instant) {
                    child.setAttribute('data-animate-state', 'active');
                    return;
                }

                if (!this.isAnimationEnabledForViewport(childSettings)) {
                    child.setAttribute('data-animate-state', 'active');
                    return;
                }

                if (!this.isElementInActivationRange(child, childSettings.threshold)) {
                    child.setAttribute('data-animate-state', 'inactive');
                    this.observeDeferredChild(child, childSettings);
                    return;
                }

                const frame = window.requestAnimationFrame(() => {
                    this.commitInactiveState(child);

                    const activeFrame = window.requestAnimationFrame(() => {
                        child.setAttribute('data-animate-state', 'active');
                        this.pendingFrames.delete(child);
                    });

                    this.pendingFrames.set(child, activeFrame);
                });

                this.cancelPendingFrame(child);
                this.pendingFrames.set(child, frame);
            });
        }

        observeDeferredChild(child, settings) {
            this.cancelPendingFrame(child);
            this.trackedElements.set(child, settings);

            if (!('IntersectionObserver' in window) || this.reducedMotion.matches) {
                this.activateElement(child, settings, true);
                return;
            }

            this.getObserver(settings.threshold).observe(child);
        }

        deactivateChildren(element) {
            this.getChildren(element).forEach((child) => {
                this.cancelPendingFrame(child);
                child.setAttribute('data-animate-state', 'inactive');
            });
        }

        stopObservingElement(element) {
            if (!this.trackedElements.has(element)) {
                return;
            }

            const settings = this.trackedElements.get(element);
            this.unobserveFromObserver(element, settings);
            this.trackedElements.delete(element);
        }

        unobserveFromObserver(element, settings) {
            const observer = this.observers.get(this.getObserverKey(settings.threshold));

            if (observer) {
                observer.unobserve(element);
            }
        }

        commitInactiveState(element) {
            element.getBoundingClientRect();
        }

        prepareElement(element, settings) {
            // All controlled elements start inactive unless the author already set a state.
            if (!element.hasAttribute('data-animate-state')) {
                element.setAttribute('data-animate-state', 'inactive');
            }

            this.applyCategoryAttributes(element, settings);

            this.getChildren(element).forEach((child, index) => {
                const childSettings = this.readSettings(child, settings, index);
                this.applySettings(child, childSettings);
                this.applyCategoryAttributes(child, childSettings);

                if (!child.hasAttribute('data-animate-state')) {
                    child.setAttribute('data-animate-state', 'inactive');
                }
            });
        }

        applySettings(element, settings) {
            // CSS variables keep timing per element without adding animation classes.
            element.style.setProperty('--cms-animate-delay', `${settings.delay}ms`);
            element.style.setProperty('--cms-animate-duration', `${settings.duration}ms`);
            element.style.setProperty('--cms-animate-easing', settings.easing);
            element.style.setProperty('--cms-animate-distance', settings.distance);
            element.style.setProperty('--cms-animate-stagger', `${settings.stagger}ms`);
            element.style.transitionProperty = 'opacity, transform, filter, clip-path';
            element.style.transitionDuration = 'var(--cms-animate-duration), var(--cms-animate-duration), var(--cms-animate-duration), var(--cms-animate-duration)';
            element.style.transitionTimingFunction = 'var(--cms-animate-easing), var(--cms-animate-easing), var(--cms-animate-easing), var(--cms-animate-easing)';
            element.style.transitionDelay = 'var(--cms-animate-delay), var(--cms-animate-delay), var(--cms-animate-delay), var(--cms-animate-delay)';
        }

        suppressTransition(element) {
            element.style.transitionProperty = 'none';
            element.style.transitionDuration = '0ms';
            element.style.transitionTimingFunction = 'linear';
            element.style.transitionDelay = '0ms';
        }

        restoreTransition(element) {
            element.style.transitionProperty = 'opacity, transform, filter, clip-path';
            element.style.transitionDuration = 'var(--cms-animate-duration), var(--cms-animate-duration), var(--cms-animate-duration), var(--cms-animate-duration)';
            element.style.transitionTimingFunction = 'var(--cms-animate-easing), var(--cms-animate-easing), var(--cms-animate-easing), var(--cms-animate-easing)';
            element.style.transitionDelay = 'var(--cms-animate-delay), var(--cms-animate-delay), var(--cms-animate-delay), var(--cms-animate-delay)';
        }

        applyCategoryAttributes(element, settings) {
            element.setAttribute('data-animate-movement', settings.movement);
            element.setAttribute('data-animate-scale', settings.scale);
            element.setAttribute('data-animate-effect', settings.effect);
            element.setAttribute('data-animate-perspective', settings.perspective);

            if (element.hasAttribute('data-animate-type')) {
                element.removeAttribute('data-animate-type');
            }
        }

        readSettings(element, parentSettings = null, childIndex = 0) {
            // Dataset values are normalised here so CSS and observer setup receive stable data.
            const dataset = element.dataset;
            const parentDelay = parentSettings ? parentSettings.delay : this.settings.delay;
            const parentStagger = parentSettings ? parentSettings.stagger : this.settings.stagger;
            const explicitDelay = this.toNumber(dataset.animateDelay, parentDelay);
            const delay = parentSettings
                ? explicitDelay + (childIndex * parentStagger)
                : explicitDelay;
            const repeat = this.toBoolean(dataset.animateRepeat, parentSettings?.repeat ?? this.settings.repeat);
            const once = repeat
                ? false
                : this.toBoolean(dataset.animateOnce, parentSettings?.once ?? this.settings.once);

            return {
                movement: this.toCategoryValue('movement', dataset.animateMovement, parentSettings?.movement ?? this.settings.movement),
                scale: this.toCategoryValue('scale', dataset.animateScale, parentSettings?.scale ?? this.settings.scale),
                effect: this.toCategoryValue('effect', dataset.animateEffect, parentSettings?.effect ?? this.settings.effect),
                perspective: this.toCategoryValue('perspective', dataset.animatePerspective, parentSettings?.perspective ?? this.settings.perspective),
                delay,
                duration: this.toNumber(dataset.animateDuration, parentSettings?.duration ?? this.settings.duration, 0),
                easing: this.toEasing(dataset.animateEasing, parentSettings?.easing ?? this.settings.easing),
                threshold: this.toThreshold(dataset.animateThreshold, parentSettings?.threshold ?? this.settings.threshold),
                stagger: this.toNumber(dataset.animateStagger, parentSettings?.stagger ?? this.settings.stagger, 0),
                distance: this.toDistance(dataset.animateDistance, parentSettings?.distance ?? this.settings.distance),
                minBreakpoint: this.toBreakpoint(dataset.animateMin, parentSettings?.minBreakpoint ?? this.settings.minBreakpoint),
                maxBreakpoint: this.toBreakpoint(dataset.animateMax, parentSettings?.maxBreakpoint ?? this.settings.maxBreakpoint),
                once,
                repeat
            };
        }

        getObserver(threshold) {
            // Observers are grouped by threshold to support per-element thresholds efficiently.
            const key = this.getObserverKey(threshold);

            if (!this.observers.has(key)) {
                this.observers.set(key, new IntersectionObserver(this.handleIntersection, {
                    root: null,
                    rootMargin: this.settings.rootMargin,
                    threshold
                }));
            }

            return this.observers.get(key);
        }

        getObserverKey(threshold) {
            return String(threshold);
        }

        getChildren(element) {
            return Array.from(element.querySelectorAll(this.settings.childSelector)).filter((child) => {
                const owner = child.closest(this.settings.selector);

                return owner === element;
            });
        }

        isElementInActivationRange(element, threshold = 0) {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

            const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
            const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
            const visibleArea = visibleWidth * visibleHeight;
            const totalArea = rect.width * rect.height;

            if (totalArea <= 0) {
                return false;
            }

            return visibleArea / totalArea >= threshold;
        }

        isAnimationEnabledForViewport(settings) {
            const width = window.innerWidth || document.documentElement.clientWidth;
            const min = this.getBreakpointValue(settings.minBreakpoint);
            const max = this.getBreakpointValue(settings.maxBreakpoint);

            if (min !== null && width < min) {
                return false;
            }

            if (max !== null && width >= this.getNextBreakpointValue(settings.maxBreakpoint)) {
                return false;
            }

            return true;
        }

        getBreakpointValue(name) {
            if (!name || !Object.prototype.hasOwnProperty.call(this.settings.breakpoints, name)) {
                return null;
            }

            return this.settings.breakpoints[name];
        }

        getNextBreakpointValue(name) {
            const keys = Object.keys(this.settings.breakpoints);
            const index = keys.indexOf(name);

            if (index === -1 || index === keys.length - 1) {
                return Number.POSITIVE_INFINITY;
            }

            return this.settings.breakpoints[keys[index + 1]];
        }

        hasAnimationCandidates(root) {
            if (!root || !root.querySelectorAll) {
                return false;
            }

            return Boolean(
                root.matches?.(this.settings.selector)
                || root.matches?.(this.settings.childSelector)
                || root.querySelector(this.settings.selector)
                || root.querySelector(this.settings.childSelector)
            );
        }

        isValidElement(element) {
            return element instanceof Element && element.hasAttribute('data-cms-animate');
        }

        watchReducedMotion() {
            // Reduced motion can change while the page is open, so the instance listens for it.
            if (this.isWatchingReducedMotion) {
                return;
            }

            this.isWatchingReducedMotion = true;

            if (this.reducedMotion.addEventListener) {
                this.reducedMotion.addEventListener('change', this.handleReducedMotionChange);
                return;
            }

            this.reducedMotion.addListener(this.handleReducedMotionChange);
        }

        unwatchReducedMotion() {
            if (!this.isWatchingReducedMotion) {
                return;
            }

            this.isWatchingReducedMotion = false;

            if (this.reducedMotion.removeEventListener) {
                this.reducedMotion.removeEventListener('change', this.handleReducedMotionChange);
                return;
            }

            this.reducedMotion.removeListener(this.handleReducedMotionChange);
        }

        watchResizeForBreakpoints(settings) {
            if (this.isWatchingResize || (!settings.minBreakpoint && !settings.maxBreakpoint)) {
                return;
            }

            this.isWatchingResize = true;
            window.addEventListener('resize', this.handleResize, { passive: true });
        }

        unwatchResize() {
            if (!this.isWatchingResize) {
                return;
            }

            this.isWatchingResize = false;
            window.removeEventListener('resize', this.handleResize);

            if (this.resizeFrame) {
                window.cancelAnimationFrame(this.resizeFrame);
                this.resizeFrame = null;
            }
        }

        handleResize() {
            if (this.resizeFrame) {
                return;
            }

            this.resizeFrame = window.requestAnimationFrame(() => {
                this.resizeFrame = null;
                this.refreshBreakpointStates();
            });
        }

        refreshBreakpointStates() {
            this.trackedElements.forEach((settings, element) => {
                if (!settings.minBreakpoint && !settings.maxBreakpoint) {
                    return;
                }

                const nextSettings = this.readSettings(element);
                this.applySettings(element, nextSettings);
                this.prepareElement(element, nextSettings);
                this.trackedElements.set(element, nextSettings);

                if (!this.isAnimationEnabledForViewport(nextSettings)) {
                    this.unobserveFromObserver(element, nextSettings);
                    this.activateElement(element, nextSettings, true);
                    return;
                }

                if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
                    this.activateElement(element, nextSettings, true);
                    return;
                }

                element.setAttribute('data-animate-state', 'inactive');
                this.deactivateChildren(element, nextSettings);
                this.getObserver(nextSettings.threshold).observe(element);
                this.activateIfInitiallyVisible(element, nextSettings);
            });
        }

        handleReducedMotionChange() {
            // When reduced motion is enabled, content is revealed immediately and remains usable.
            if (this.reducedMotion.matches) {
                this.trackedElements.forEach((settings, element) => {
                    const observer = this.observers.get(this.getObserverKey(settings.threshold));

                    if (observer) {
                        observer.unobserve(element);
                    }

                    this.activateElement(element, settings, true);
                });

                return;
            }

            if (!('IntersectionObserver' in window)) {
                return;
            }

            this.trackedElements.forEach((settings, element) => {
                this.getObserver(settings.threshold).observe(element);
            });
        }

        cancelPendingFrame(element) {
            const frame = this.pendingFrames.get(element);

            if (frame) {
                window.cancelAnimationFrame(frame);
                this.pendingFrames.delete(element);
            }
        }

        toCategoryValue(category, value, fallback) {
            const candidate = typeof value === 'string' && value.trim() ? value.trim() : fallback;
            const allowed = this.allowedValues[category];

            if (allowed?.has(candidate)) {
                return candidate;
            }

            return category === 'effect' ? 'fade' : 'none';
        }

        toNumber(value, fallback, minimum = null) {
            const parsed = Number.parseInt(value, 10);

            if (!Number.isFinite(parsed)) {
                return fallback;
            }

            if (minimum !== null && parsed < minimum) {
                return fallback;
            }

            return parsed;
        }

        toThreshold(value, fallback) {
            const parsed = Number.parseFloat(value);

            if (!Number.isFinite(parsed)) {
                return fallback;
            }

            return Math.min(Math.max(parsed, 0), 1);
        }

        toBoolean(value, fallback) {
            if (value === 'true') {
                return true;
            }

            if (value === 'false') {
                return false;
            }

            return fallback;
        }

        toBreakpoint(value, fallback) {
            const candidate = typeof value === 'string' && value.trim() ? value.trim() : fallback;

            if (!candidate) {
                return null;
            }

            return Object.prototype.hasOwnProperty.call(this.settings.breakpoints, candidate) ? candidate : null;
        }

        toDistance(value, fallback) {
            if (typeof value !== 'string' || !value.trim()) {
                return fallback;
            }

            return value.trim();
        }

        toEasing(value, fallback) {
            if (typeof value !== 'string' || !value.trim()) {
                return fallback;
            }

            return value.trim();
        }
    }

    window.cmsAnimationLibrary = cmsAnimationLibrary;

    function bootCmsAnimationLibrary() {
        // The global instance prevents duplicate auto-initialisation across repeated script loads.
        if (window.cmsAnimationLibraryInstance?.isCmsAnimationLibraryInstance) {
            window.cmsAnimationLibraryInstance.init();
            return;
        }

        window.cmsAnimationLibraryInstance = new cmsAnimationLibrary();
        window.cmsAnimationLibraryInstance.init();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootCmsAnimationLibrary, { once: true });
    } else {
        bootCmsAnimationLibrary();
    }
})();
